import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent, EVOTRUX_LINK_BLUE } from "constants/index";

interface Props {
  label: string;
  note: string;
  quoteId: number;
  shipmentId: number;
}

const CLAMPED_LINES = 2;

// The note spans five columns, so on a wide monitor an unbounded line runs far
// past a comfortable reading measure.
const MAX_LINE_LENGTH_PX = 760;

const CLAMPED_SX = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: CLAMPED_LINES,
  overflow: "hidden",
} as const;

/**
 * A carrier's note, on its own row under the quote it belongs to.
 *
 * Clamped to two lines. CSS clamping reports nothing about whether it actually
 * cut anything, so the element measures itself and only offers "Show more" when
 * there is genuinely more to read.
 */
export function QuoteNote(props: Props) {
  const { label, note, quoteId, shipmentId } = props;
  const posthog = usePostHog();

  const textRef = useRef<HTMLParagraphElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const measure = useCallback(() => {
    const element = textRef.current;
    if (element === null) {
      return;
    }
    // Only meaningful while clamped: expanded, the two heights always agree.
    setIsOverflowing(element.scrollHeight > element.clientHeight);
  }, []);

  useLayoutEffect(() => {
    if (isExpanded) {
      return undefined;
    }
    measure();

    // Reflows come from the window and from the column widths settling, and the
    // second never fires a resize event — hence both.
    window.addEventListener("resize", measure);
    const observer = new ResizeObserver(measure);
    if (textRef.current !== null) {
      observer.observe(textRef.current);
    }

    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [measure, isExpanded, note]);

  return (
    <Stack direction="row" sx={{ maxWidth: MAX_LINE_LENGTH_PX }}>
      <Box
        sx={{
          width: 3,
          backgroundColor: "divider",
          mr: 1,
          flexShrink: 0,
          alignSelf: "stretch",
        }}
      />
      <Stack direction="column" spacing={0.25}>
        <Typography
          ref={textRef}
          variant="body2"
          sx={{ lineHeight: 1.4, ...(isExpanded ? {} : CLAMPED_SX) }}
        >
          <span style={{ fontWeight: "bold" }}>{label}</span>
          {note}
        </Typography>
        {(isOverflowing || isExpanded) && (
          <Tooltip
            describeChild
            title={
              isExpanded
                ? "Collapse the note back to two lines"
                : "Read the carrier's full note"
            }
          >
            <Typography
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                // Counts how often two lines is not enough, to tune the clamp.
                if (!isExpanded) {
                  posthog?.capture(ClientAppEvent.SHIPPER_QUOTE_NOTE_EXPANDED, {
                    quote_id: quoteId,
                    shipment_id: shipmentId,
                  });
                }
                setIsExpanded((expanded) => !expanded);
              }}
              sx={{
                alignSelf: "flex-start",
                background: "none",
                border: "none",
                p: 0,
                cursor: "pointer",
                fontWeight: 500,
                color: EVOTRUX_LINK_BLUE,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {isExpanded ? "Show less" : "Show more"}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}
